import { useState, useEffect } from "react";
import {
  Loader2,
  Search,
  CheckCircle2,
  Send,
  Mic,
  Trash2,
  Copy,
  Monitor,
} from "lucide-react";
import { colors, fonts } from "../../styles/theme";
import { useAuth } from "../../hooks/useAuth";
import { Badge, Button } from "../../components/ui";
import {
  fetchContacts,
  fetchMyAllowedSendAs,
  addZoomPanelists,
  removeZoomPanelist,
  sendZoomInvitations,
  requestTechCheck,
} from "../../services/api";
import { getGraphToken } from "../../services/msal";
import { inputStyle } from "./constants";

export default function PanelistsPanel({ event, panelists, onRefresh }) {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [sendingInvite, setSendingInvite] = useState(null);
  const [requestingTechCheck, setRequestingTechCheck] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);
  const [sendMethod, setSendMethod] = useState("o365");
  const [senderEmail, setSenderEmail] = useState("");
  const [allowedSendAs, setAllowedSendAs] = useState([]);

  useEffect(() => {
    fetchMyAllowedSendAs()
      .then((data) => {
        setAllowedSendAs(data.allowedEmails || []);
        if (data.allowedEmails?.length > 0)
          setSenderEmail(data.allowedEmails[0]);
      })
      .catch(() => setAllowedSendAs([]));
  }, []);

  // Debounced search
  useEffect(() => {
    if (!search.trim()) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const existingEmails = new Set(panelists.map((p) => p.email.toLowerCase()));
    const timer = setTimeout(() => {
      fetchContacts({ search: search.trim(), limit: 8 })
        .then((data) => {
          if (!cancelled) {
            const filtered = (data.contacts || []).filter(
              (c) => c.email && !existingEmails.has(c.email.toLowerCase()),
            );
            setResults(filtered);
          }
        })
        .catch(() => {})
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search, panelists]);

  const handleAdd = async (contact) => {
    setAdding(contact.id);
    setError(null);
    try {
      await addZoomPanelists(event.id, [
        {
          contactId: contact.id,
          email: contact.email,
          firstName: contact.firstName,
          lastName: contact.lastName,
          company: contact.company,
        },
      ]);
      setSearch("");
      onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(null);
    }
  };

  const handleRemove = async (email) => {
    if (!window.confirm("Remove this panelist from the webinar?")) return;
    setRemoving(email);
    try {
      await removeZoomPanelist(event.id, email);
      onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setRemoving(null);
    }
  };

  const handleSendInvite = async (pan) => {
    setSendingInvite(pan.email);
    setError(null);
    try {
      let graphToken = null;
      if (sendMethod === "o365") graphToken = await getGraphToken();

      await sendZoomInvitations(event.id, {
        contacts: [
          {
            contactId: pan.contactId,
            email: pan.email,
            firstName: pan.firstName,
            lastName: pan.lastName,
            company: pan.company,
          },
        ],
        role: "panelist",
        resend: true,
        sendMethod,
        senderEmail: sendMethod === "o365" ? senderEmail : undefined,
        graphToken,
      });
      onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setSendingInvite(null);
    }
  };

  const handleCopyLink = (pan) => {
    if (!pan.joinUrl) return;
    navigator.clipboard.writeText(pan.joinUrl);
    setCopied(pan.email);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleTechCheck = async (pan) => {
    setRequestingTechCheck(pan.email);
    setError(null);
    try {
      const name = [pan.firstName, pan.lastName].filter(Boolean).join(" ") || pan.email;
      await requestTechCheck(event.id, { panelistEmail: pan.email, panelistName: name });
      setRequestingTechCheck("done-" + pan.email);
      setTimeout(() => setRequestingTechCheck(null), 2000);
    } catch (err) {
      setError(err.message);
      setRequestingTechCheck(null);
    }
  };

  const STATUS_BADGE = {
    added: { label: "Added", color: "#f59e0b", bg: "rgba(245,158,11,0.10)" },
    invited: {
      label: "Invited",
      color: colors.info,
      bg: "rgba(99,102,241,0.10)",
    },
    confirmed: {
      label: "Confirmed",
      color: colors.success,
      bg: "rgba(16,185,129,0.10)",
    },
    removed: {
      label: "Removed",
      color: colors.textSecondary,
      bg: "rgba(107,114,128,0.10)",
    },
  };

  const activePanelists = panelists.filter((p) => p.status !== "removed");

  return (
    <div>
      {/* Add panelists - only for scheduled events */}
      {event.status === "scheduled" && (
        <div
          style={{
            marginBottom: 16,
            borderBottom: `1px solid ${colors.border}`,
            paddingBottom: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginBottom: 8,
            }}
          >
            <Mic size={13} color={colors.accent} />
            <span style={{ fontSize: 12, fontWeight: 600, color: colors.text }}>
              Add Panelist
            </span>
          </div>

          {/* Send method toggle for invitations */}
          <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
            {[
              { id: "o365", label: "Personal O365" },
              { id: "sendgrid", label: "SendGrid" },
            ].map((m) => (
              <button
                key={m.id}
                onClick={() => setSendMethod(m.id)}
                style={{
                  padding: "3px 8px",
                  borderRadius: 4,
                  fontSize: 10,
                  fontWeight: 600,
                  fontFamily: fonts.body,
                  border: `1px solid ${sendMethod === m.id ? colors.accent : colors.border}`,
                  background:
                    sendMethod === m.id ? colors.accentDim : "transparent",
                  color: sendMethod === m.id ? colors.accent : colors.textSecondary,
                  cursor: "pointer",
                }}
              >
                {m.label}
              </button>
            ))}
          </div>

          {/* Sender dropdown for O365 */}
          {sendMethod === "o365" && allowedSendAs.length > 1 && (
            <select
              value={senderEmail}
              onChange={(e) => setSenderEmail(e.target.value)}
              style={{
                ...inputStyle,
                fontSize: 11,
                padding: "5px 8px",
                marginBottom: 8,
                cursor: "pointer",
                appearance: "auto",
              }}
            >
              {allowedSendAs.map((email) => (
                <option key={email} value={email}>
                  {email}
                  {email === user?.userDetails?.toLowerCase() ? " (you)" : ""}
                </option>
              ))}
            </select>
          )}

          {/* Search box */}
          <div style={{ position: "relative" }}>
            <Search
              size={13}
              color={colors.textSecondary}
              style={{ position: "absolute", left: 10, top: 10 }}
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search contacts to add as panelist..."
              style={{ ...inputStyle, paddingLeft: 30, fontSize: 12 }}
            />
          </div>

          {/* Search results */}
          {search.trim() && (
            <div
              style={{
                marginTop: 6,
                maxHeight: 200,
                overflowY: "auto",
                border: `1px solid ${colors.border}`,
                borderRadius: 6,
              }}
            >
              {searching ? (
                <div
                  style={{
                    padding: 12,
                    textAlign: "center",
                    color: colors.textSecondary,
                  }}
                >
                  <Loader2
                    size={14}
                    className="spin"
                  />
                </div>
              ) : results.length === 0 ? (
                <div
                  style={{
                    padding: 12,
                    textAlign: "center",
                    color: colors.textSecondary,
                    fontSize: 12,
                  }}
                >
                  {search.trim()
                    ? "No matching contacts (or already a panelist)"
                    : ""}
                </div>
              ) : (
                results.map((c) => (
                  <div
                    key={c.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "8px 10px",
                      borderBottom: `1px solid ${colors.borderLight}`,
                    }}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: colors.text,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {[c.firstName, c.lastName].filter(Boolean).join(" ") ||
                          c.email}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: colors.textSecondary,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {c.email}
                        {c.company ? ` — ${c.company}` : ""}
                      </div>
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleAdd(c)}
                      style={{
                        padding: "3px 10px",
                        fontSize: 11,
                        minWidth: 0,
                        opacity: adding ? 0.5 : 1,
                      }}
                      disabled={!!adding}
                    >
                      {adding === c.id ? (
                        <Loader2
                          size={12}
                          className="spin"
                        />
                      ) : (
                        <>
                          <Mic size={11} /> Add
                        </>
                      )}
                    </Button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {error && (
        <div
          style={{
            fontSize: 11,
            color: colors.danger,
            marginBottom: 8,
            padding: "6px 10px",
            background: "rgba(239,68,68,0.08)",
            borderRadius: 6,
          }}
        >
          {error}
        </div>
      )}

      {/* Panelist list */}
      {activePanelists.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: 32,
            color: colors.textSecondary,
            fontSize: 13,
          }}
        >
          No panelists added yet. Search for contacts above to add panelists.
        </div>
      ) : (
        activePanelists.map((pan) => {
          const st = STATUS_BADGE[pan.status] || STATUS_BADGE.added;
          return (
            <div
              key={pan.email}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 0",
                borderBottom: `1px solid ${colors.borderLight}`,
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: colors.text,
                  }}
                >
                  {[pan.firstName, pan.lastName].filter(Boolean).join(" ") ||
                    pan.email}
                </div>
                <div style={{ fontSize: 12, color: colors.textSecondary }}>
                  {pan.email}
                  {pan.company && (
                    <span
                      style={{
                        marginLeft: 6,
                        fontSize: 11,
                        color: colors.textSecondary,
                      }}
                    >
                      — {pan.company}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Badge color={st.color} bg={st.bg}>
                  {st.label}
                </Badge>

                {/* Copy join link */}
                {pan.joinUrl && (
                  <button
                    onClick={() => handleCopyLink(pan)}
                    title="Copy panelist join link"
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 4,
                      color:
                        copied === pan.email
                          ? colors.success
                          : colors.textSecondary,
                    }}
                  >
                    {copied === pan.email ? (
                      <CheckCircle2 size={14} />
                    ) : (
                      <Copy size={14} />
                    )}
                  </button>
                )}

                {/* Request tech check */}
                {event.status === "scheduled" && (
                  <button
                    onClick={() => handleTechCheck(pan)}
                    title="Request IT tech check"
                    disabled={!!requestingTechCheck}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 4,
                      color: requestingTechCheck === "done-" + pan.email
                        ? colors.success
                        : colors.warning,
                      opacity: requestingTechCheck && requestingTechCheck !== "done-" + pan.email ? 0.5 : 1,
                    }}
                  >
                    {requestingTechCheck === pan.email ? (
                      <Loader2 size={14} className="spin" />
                    ) : requestingTechCheck === "done-" + pan.email ? (
                      <CheckCircle2 size={14} />
                    ) : (
                      <Monitor size={14} />
                    )}
                  </button>
                )}

                {/* Send / resend panelist invitation */}
                {(pan.status === "added" || pan.status === "invited") && event.status === "scheduled" && (
                  <button
                    onClick={() => handleSendInvite(pan)}
                    title={pan.status === "invited" ? "Resend panelist invitation" : "Send panelist invitation"}
                    disabled={!!sendingInvite}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 4,
                      color: colors.info,
                      opacity: sendingInvite ? 0.5 : 1,
                    }}
                  >
                    {sendingInvite === pan.email ? (
                      <Loader2
                        size={14}
                        className="spin"
                      />
                    ) : (
                      <Send size={14} />
                    )}
                  </button>
                )}

                {/* Remove */}
                {event.status === "scheduled" && (
                  <button
                    onClick={() => handleRemove(pan.email)}
                    title="Remove panelist"
                    disabled={!!removing}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 4,
                      color: colors.danger,
                      opacity: removing === pan.email ? 0.5 : 1,
                    }}
                  >
                    {removing === pan.email ? (
                      <Loader2
                        size={14}
                        className="spin"
                      />
                    ) : (
                      <Trash2 size={14} />
                    )}
                  </button>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
